import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown, CreditCard, Database, Globe, Server, ShieldCheck, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { AppHeader } from "../../../components/app-header";
import { Button } from "../../../components/ui/button";
import { InlineError, Skeleton } from "../../../components/ui/feedback";
import { notify } from "../../../components/ui/toast";
import { account } from "../../../copy/home";
import { type AccountEstimate, api, type CheckoutMode, errorMessage, type PriceLine } from "../../../lib/api";
import { cn } from "../../../lib/cn";
import { useDelayedFlag } from "../../../lib/hooks";
import { useOnline } from "../../../lib/online";
import { billingQuery } from "../../../lib/queries";

type Checkout = "success" | "cancelled";

export const Route = createFileRoute("/_authed/account/billing")({
  validateSearch: (search: Record<string, unknown>): { checkout?: Checkout } => {
    const c = search.checkout;
    return c === "success" || c === "cancelled" ? { checkout: c } : {};
  },
  component: BillingPage,
});

const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/** A soft rounded card, the same surface the rest of the app uses. */
function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={cn("rounded-2xl border border-border bg-card shadow-card", className)}>{children}</section>;
}

/** A small, human (not shouty) field label. */
function FieldLabel({ children }: { children: ReactNode }) {
  return <p className="text-[13px] font-medium text-muted-foreground">{children}</p>;
}

/** The icon chip for a price line — a quiet signifier of what the money is for. */
function LineIcon({ lineKey }: { lineKey: PriceLine["key"] }) {
  const map = {
    static: { Icon: Globe, tone: "bg-surface-2 text-muted-foreground" },
    server: { Icon: Server, tone: "bg-primary-soft text-primary" },
    database: { Icon: Database, tone: "bg-surface-2 text-muted-foreground" },
    ai_managed: { Icon: Sparkles, tone: "bg-primary-soft text-primary" },
  } as const;
  const { Icon, tone } = map[lineKey];
  return (
    <span className={cn("flex size-10 shrink-0 items-center justify-center rounded-xl", tone)} aria-hidden="true">
      <Icon className="size-5" />
    </span>
  );
}

function Amount({ cents }: { cents: number }) {
  const copy = account.billingPage;
  if (cents === 0) return <span className="rounded-full bg-success/10 px-2.5 py-1 text-xs font-semibold text-success">{copy.freeLabel}</span>;
  return (
    <span className="shrink-0 text-sm font-semibold tabular-nums">
      {usd(cents)}
      <span className="font-normal text-muted-foreground">{copy.perMonth}</span>
    </span>
  );
}

function Line({ line }: { line: PriceLine }) {
  const copy = account.billingPage;
  const [open, setOpen] = useState(false);
  return (
    <div className="flex gap-4 py-5 first:pt-0 last:pb-0">
      <LineIcon lineKey={line.key} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm font-medium leading-6">{line.title}</p>
          <Amount cents={line.amountCents} />
        </div>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{line.why}</p>
        <button
          type="button"
          className="mt-2.5 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? copy.specHide : copy.specShow}
          <ChevronDown className={cn("size-3.5 transition-transform duration-200", open && "rotate-180")} aria-hidden="true" />
        </button>
        {open && <p className="mt-2.5 rounded-xl bg-surface-2 px-3.5 py-3 text-xs leading-relaxed text-muted-foreground">{line.spec}</p>}
      </div>
    </div>
  );
}

/**
 * The guaranteed monthly total, itemized per app. Every paid line explains
 * itself in plain words framed around sharing the app, with the technical spec
 * one tap away (for the curious, and for the AI).
 */
function CostEstimate({ estimate }: { estimate: AccountEstimate }) {
  const copy = account.billingPage;

  return (
    <div>
      <h2 className="text-sm font-semibold tracking-tight">{copy.estimateTitle}</h2>
      <p className="mt-1.5 mb-4 max-w-prose text-sm leading-relaxed text-muted-foreground">{copy.estimateIntro}</p>

      {estimate.apps.length === 0 ? (
        <Card className="flex flex-col items-center gap-4 px-6 py-12 text-center">
          <span className="flex size-14 items-center justify-center rounded-full bg-success/10 text-success" aria-hidden="true">
            <ShieldCheck className="size-7" />
          </span>
          <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">{copy.estimateEmpty}</p>
        </Card>
      ) : (
        <Card>
          <ul className="flex flex-col">
            {estimate.apps.map((app, i) => (
              <li key={app.appId} className={cn("px-6 py-6", i > 0 && "border-t border-border")}>
                <p className="mb-2 text-sm font-semibold">{app.name}</p>
                <div className="flex flex-col divide-y divide-border">
                  {app.lines.map((line) => (
                    <Line key={`${app.appId}:${line.key}`} line={line} />
                  ))}
                </div>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between gap-4 rounded-b-2xl border-t border-border bg-surface px-6 py-6">
            <div>
              <p className="text-sm font-semibold">{copy.totalLabel}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{copy.totalFlatNote}</p>
            </div>
            <p className="text-2xl font-semibold tabular-nums">
              {usd(estimate.monthlyCents)}
              <span className="text-sm font-normal text-muted-foreground">{copy.perMonth}</span>
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

/** Billing (UX_SPEC §11). Card data never touches Carno — Stripe Checkout/Portal only. */
function BillingPage() {
  const online = useOnline();
  const reduce = useReducedMotion() === true;
  const { checkout } = Route.useSearch();
  const navigate = Route.useNavigate();
  const billing = useQuery(billingQuery);
  const showSkeleton = useDelayedFlag(billing.isPending, 150);
  const copy = account.billingPage;

  // Surface the ?checkout= result once, then drop it from the URL.
  useEffect(() => {
    if (!checkout) return;
    if (checkout === "success") notify.success(copy.checkoutSuccess);
    else notify.info(copy.checkoutCancelled);
    void navigate({ search: {}, replace: true });
  }, [checkout, copy.checkoutSuccess, copy.checkoutCancelled, navigate]);

  const checkoutMut = useMutation({
    mutationFn: (mode: CheckoutMode) => api.startCheckout(mode),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (e) => notify.error(errorMessage(e, copy.actionFailed)),
  });
  const portalMut = useMutation({
    mutationFn: () => api.openBillingPortal(),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (e) => notify.error(errorMessage(e, copy.actionFailed)),
  });
  const busy = checkoutMut.isPending || portalMut.isPending;
  const rise = reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.12 } }
    : { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as const } };

  const d = billing.data;

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <main id="main" className="mx-auto max-w-2xl px-5 pt-10 pb-24 sm:px-6 sm:pt-12">
        <h1 className="text-2xl font-semibold tracking-tight">{copy.pageTitle}</h1>
        <p className="mt-2 mb-10 max-w-prose text-sm leading-relaxed text-muted-foreground">{copy.intro}</p>

        {billing.isPending ? (
          showSkeleton ? (
            <div className="flex flex-col gap-7">
              <Skeleton className="h-24 w-full rounded-2xl" />
              <Skeleton className="h-72 w-full rounded-2xl" />
            </div>
          ) : null
        ) : billing.isError || !d ? (
          <InlineError message={copy.loadFailed} onRetry={() => void billing.refetch()} />
        ) : !d.billingEnabled ? (
          <Card className="px-6 py-12 text-center text-sm text-muted-foreground">{copy.notEnabled}</Card>
        ) : (
          <motion.div {...rise} className="flex flex-col gap-7">
            {/* Attention: a payment issue or the free-limit gate. One elegant callout. */}
            {(d.delinquent || d.gate.required) && (
              <div className="flex flex-col gap-4 rounded-2xl border border-warn/30 bg-warn-soft/60 p-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4">
                  <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-warn/15 text-warn" aria-hidden="true">
                    <CreditCard className="size-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">{d.delinquent ? copy.delinquent : copy.gateCtaTitle}</p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {d.delinquent ? copy.noChargeNote : d.gate.reason === "too_many_resources" ? copy.gateTooMany(d.gate.limit) : copy.gateGrace}
                    </p>
                  </div>
                </div>
                <Button variant="primary" className="shrink-0 rounded-xl" disabled={!online || busy} onClick={() => checkoutMut.mutate("setup")}>
                  {copy.addCard}
                </Button>
              </div>
            )}

            {/* Plan + payment method — one quiet status card. */}
            <Card>
              <div className="flex items-center justify-between gap-4 px-6 py-5">
                <div>
                  <FieldLabel>{copy.planLabel}</FieldLabel>
                  <p className="mt-1 text-base font-semibold">{d.plan === "pro" ? copy.plans.pro : copy.plans.free}</p>
                </div>
                {d.plan !== "pro" && (
                  <Button variant="secondary" size="sm" className="rounded-lg" disabled={!online || busy} onClick={() => checkoutMut.mutate("subscription")}>
                    {copy.upgrade}
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between gap-4 border-t border-border px-6 py-5">
                <div className="flex items-center gap-4">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-surface-2 text-muted-foreground" aria-hidden="true">
                    <CreditCard className="size-5" />
                  </span>
                  <div>
                    <FieldLabel>{copy.paymentMethod}</FieldLabel>
                    <p className="mt-1 text-sm">{d.hasPaymentMethod && d.cardBrand && d.cardLast4 ? copy.cardOnFile(d.cardBrand, d.cardLast4) : copy.noCard}</p>
                  </div>
                </div>
                {d.hasCustomer ? (
                  <Button variant="secondary" size="sm" className="rounded-lg" disabled={!online || busy} onClick={() => portalMut.mutate()}>
                    {copy.manage}
                  </Button>
                ) : (
                  !d.gate.required &&
                  !d.delinquent && (
                    <Button variant="secondary" size="sm" className="rounded-lg" disabled={!online || busy} onClick={() => checkoutMut.mutate("setup")}>
                      {copy.addCard}
                    </Button>
                  )
                )}
              </div>
            </Card>

            <CostEstimate estimate={d.estimate} />
          </motion.div>
        )}
      </main>
    </div>
  );
}
